import { useState } from "react";
import axios from "axios";
import PageShell, { PageHeader } from "@/components/PageShell";
import Seo from "@/components/Seo";
import { Sparkles, Newspaper, Music, Trophy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_SITE_URL || "https://nematv.com";

const highlights = [
  { icon: Newspaper, title: "Arabic news",       body: "Live news bulletins in Arabic — regional and international." },
  { icon: Sparkles,  title: "Prime-time drama",  body: "Ramadan specials, series and cinema from across the Arab world." },
  { icon: Trophy,    title: "Sport",             body: "Football, motorsport and the biggest live sporting moments." },
  { icon: Music,     title: "Music & culture",   body: "Classical, chaabi and modern hits — plus travel and cooking shows." },
];

const ArabicTVPage = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      const { data } = await axios.post(`${API}/arabic-waitlist`, { email });
      const label = {
        subscribed: "You're on the Arabic TV waitlist. We'll be first to let you know at launch.",
        already_subscribed: "You're already on the waitlist — thanks!",
        resubscribed: "Welcome back — we've reactivated your waitlist spot.",
      }[data.status] || "Added to the waitlist.";
      setStatus({ state: "success", msg: label });
      setEmail("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setStatus({ state: "error", msg: typeof d === "string" ? d : "Could not sign you up. Please try again." });
    }
  };

  return (
    <PageShell>
      <Seo
        title="Arabic TV Package (Coming Soon) — Live Arab World Channels | Nema TV"
        description="A dedicated Arabic TV Package is coming soon to Nema TV — live news, drama, sport and music from across the Arab world. Join the waitlist to be first when it launches."
        keywords="arabic tv streaming, live arabic channels, arab world tv, ramadan drama live, arabic news online, mena iptv, nema tv arabic"
        path="/packages/arabic-tv"
        schemas={[{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home",     item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Packages" },
            { "@type": "ListItem", position: 3, name: "Arabic TV", item: `${SITE_URL}/packages/arabic-tv` },
          ],
        }]}
      />
      <PageHeader
        eyebrow="Arabic TV Package · Coming soon"
        title="Arabic TV, next on the guide."
        subtitle="A dedicated Arabic TV Package is next on the Nema TV roadmap. Drop your email to be first when it launches."
      />

      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <div
            className="relative overflow-hidden rounded-3xl min-h-[420px] p-8 md:p-10 flex items-end"
            style={{
              background:
                "linear-gradient(135deg, rgba(107,33,217,0.35) 0%, rgba(34,211,238,0.25) 100%), url('https://images.unsplash.com/photo-1518684079-3c830dcef090?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600') center/cover no-repeat",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-[#05030A]/85 via-[#05030A]/40 to-transparent" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#22D3EE]/40 bg-[#22D3EE]/15 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-[#67e8f9] mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-[#22D3EE] animate-pulse" />
                Coming soon
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold text-white leading-tight">
                From Beirut to Casablanca,<br /> <span className="gradient-text">live in your living room.</span>
              </h2>
              <p className="mt-4 text-white/80 max-w-md">
                Live news, prime-time drama, sport and culture from across the
                Arab world — details announced closer to launch.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div className="rounded-3xl border border-white/10 bg-[#0F0A19] p-6 md:p-8">
              <div className="text-xs uppercase tracking-[0.25em] text-[#22D3EE] font-semibold mb-3">Join the waitlist</div>
              <h3 className="font-display text-2xl md:text-3xl font-extrabold leading-tight">Be first to know when Arabic TV goes live.</h3>
              <form onSubmit={submit} data-testid="arabic-waitlist-form" className="mt-5 flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    data-testid="arabic-waitlist-email"
                    required type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="flex-1 bg-white/5 border border-white/10 rounded-full px-5 py-3 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#22D3EE] focus:border-transparent"
                  />
                  <button
                    type="submit"
                    data-testid="arabic-waitlist-submit"
                    disabled={status.state === "loading"}
                    className="rounded-full bg-gradient-to-r from-[#6B21D9] to-[#22D3EE] text-white px-6 py-3 text-sm font-semibold hover:shadow-[0_0_25px_rgba(34,211,238,0.4)] transition-shadow disabled:opacity-60"
                  >
                    {status.state === "loading" ? "…" : "Notify me"}
                  </button>
                </div>
                {status.state === "success" && <p data-testid="arabic-waitlist-success" className="text-xs text-[#a7f3f9]">{status.msg}</p>}
                {status.state === "error"   && <p data-testid="arabic-waitlist-error"   className="text-xs text-red-300">{status.msg}</p>}
              </form>
            </div>

            <div className="rounded-3xl border border-white/10 bg-[#0F0A19] p-6 md:p-8">
              <div className="text-xs uppercase tracking-[0.25em] text-white/50 font-semibold mb-4">What to expect</div>
              <div className="grid grid-cols-2 gap-4">
                {highlights.map((h) => (
                  <div key={h.title} className="flex flex-col gap-2">
                    <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                      <h.icon className="w-4 h-4 text-[#22D3EE]" />
                    </div>
                    <div className="text-white font-semibold text-sm">{h.title}</div>
                    <div className="text-white/55 text-xs">{h.body}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default ArabicTVPage;
